import type { AvlItem } from '@/services/approvedVendorList';
import { PalmettoTag } from '@palmetto/dsm-components';
import { useMemo } from 'react';

import { ProgramTypeLabels } from 'types';
import type { FeocDataItem } from 'types';

import {
  type AvlItemLike,
  type FieldSpec,
  baseFields,
  emptyValueLabel,
  equipmentFields,
  formatFieldValue,
} from './avlFieldRegistry';

const labelFor = (fieldName: string, code: string): string => {
  if (fieldName === 'programTypes') {
    return ProgramTypeLabels[code as keyof typeof ProgramTypeLabels] ?? code;
  }
  return code;
};

const feocRowFields: { key: keyof FeocDataItem; label: string }[] = [
  { key: 'subSku', label: 'Sub-SKU' },
  { key: 'mlpeDcPercentage', label: 'DC % MLPE' },
  { key: 'stringDcPercentage', label: 'DC % String' },
  { key: 'mlpeFeocPercentage', label: 'FEOC % MLPE' },
  { key: 'stringFeocPercentage', label: 'FEOC % String' },
  { key: 'bessDcPercentage', label: 'BESS DC %' },
  { key: 'bessFeocPercentage', label: 'BESS FEOC %' },
];

const FeocDataView = ({ rows }: { rows: FeocDataItem[] }) => {
  if (rows.length === 0) {
    return <div className="body-300 text-text-secondary">None</div>;
  }
  return (
    <div className="flex flex-col gap-8">
      {rows.map((row, index) => (
        <div
          key={`${row.vintage}-${index}`}
          className="flex flex-col gap-4 rounded-8 border border-border-default p-8"
        >
          <div className="body-bold-300 text-text-primary">Vintage: {row.vintage || emptyValueLabel}</div>
          <div className="flex flex-row flex-wrap gap-x-16 gap-y-4">
            {feocRowFields.map(({ key, label }) => {
              const value = row[key];
              if (value === undefined || value === null || value === '') return null;
              return (
                <div key={key} className="body-300 text-text-secondary">
                  <span className="body-bold-300 text-text-primary">{label}:</span> {String(value)}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const AvlFieldView = ({ spec, item }: { spec: FieldSpec; item: AvlItem }) => {
  if (spec.kind === 'section') {
    return (
      <div className="col-span-3 border-b border-border-default pb-4">
        <span className="body-bold-300 text-text-secondary">{spec.label}</span>
      </div>
    );
  }

  const formatted = formatFieldValue(spec, item as unknown as AvlItemLike, labelFor);
  const isWide = spec.kind === 'feocData' || spec.kind === 'textarea';

  return (
    <div className={`flex flex-col gap-4${isWide ? ' col-span-2' : ''}`}>
      <div className="body-300 text-text-secondary">{spec.label}</div>
      {formatted.display === 'text' && <div className="body-bold-300 text-text-primary">{formatted.value}</div>}
      {formatted.display === 'tags' && (
        <div className="flex flex-row flex-wrap gap-4">
          {formatted.values.length ? (
            formatted.values.map((value) => <PalmettoTag key={value} label={value} />)
          ) : (
            <span className="body-bold-300 text-text-primary">{emptyValueLabel}</span>
          )}
        </div>
      )}
      {formatted.display === 'aliases' && (
        <div className="flex flex-col gap-4">
          {formatted.values.length ? (
            formatted.values.map((alias) => (
              <div key={`${alias.source}-${alias.value}`} className="body-300 text-text-primary">
                {alias.source ? (
                  <>
                    <span className="body-bold-300">{alias.source}</span>: {alias.value}
                  </>
                ) : (
                  alias.value
                )}
              </div>
            ))
          ) : (
            <span className="body-300 text-text-secondary">No aliases defined</span>
          )}
        </div>
      )}
      {formatted.display === 'feocData' && <FeocDataView rows={formatted.rows} />}
    </div>
  );
};

/**
 * Registry-driven read-only view for an AVL item. Renders base fields + per-type
 * spec fields for the item's equipment type — the same field set the edit form
 * uses — so the view can never miss an equipment-specific property.
 */
export const AvlItemViewFields = ({ item }: { item: AvlItem }) => {
  const activeFields = useMemo<FieldSpec[]>(
    () => [...baseFields, ...equipmentFields[item.equipmentType]],
    [item.equipmentType],
  );

  return (
    <div className="grid grid-cols-3 gap-x-16 gap-y-16">
      {activeFields.map((spec) => (
        <AvlFieldView key={spec.name} spec={spec} item={item} />
      ))}
    </div>
  );
};
